// Cliente tipado de Equipo — spec docs/superpowers/specs/2026-09-11-invitaciones-equipo-design.md §2.1.
// Traduce el contrato snake_case del backend a camelCase y los errores a `EquipoError.tipo`.

import { ATLAS_BACKEND_URL } from '../config';
import { atlasBackendRequest } from './atlasBackend';
import { supabase } from './supabase';
import { olvidarNombresDelEquipo } from '../hooks/useNombresDelEquipo';
import { olvidarPermisos, type MisPermisos } from './permisosDelEquipo';

export interface MiembroEquipo {
  id: string;
  nombre: string;
  email: string | null;
  esDueno: boolean;
  unidoAt: string | null;
  /** Los que le valen (el dueño, siempre todos) — ADR-2026-10-19-permisos-por-miembro-del-equipo. */
  permisos: MisPermisos;
}

export interface MarcaDeLaCuenta {
  id: string;
  nombre: string;
}

export interface LinkEquipo {
  token: string;
  expiraAt: string;
  usos: number;
  estado: 'vigente' | 'vencido';
}

export interface Equipo {
  soyDueno: boolean;
  /** Personas en total que permite el plan, dueño incluido; `null` = sin límite (Corporativo). */
  tope: number | null;
  /**
   * ¿El plan deja sumar gente? Sólo los planes de equipo (ADR-2026-10-27-planes-de-equipo-en-el-front).
   * `null` si el backend todavía no lo manda: la pantalla decide con el plan.
   */
  incluyeEquipo: boolean | null;
  miembros: MiembroEquipo[];
  /** Sólo viene para el dueño; para un miembro es `null` siempre. */
  link: LinkEquipo | null;
  /** Las marcas de la cuenta, para elegir cuáles ve cada miembro. Sólo para el dueño anotado. */
  marcas: MarcaDeLaCuenta[] | null;
}

export class EquipoError extends Error {
  constructor(
    /** `error_type` del backend: forbidden · plan_sin_equipo · es_el_dueno · not_found · red · … */
    readonly tipo: string,
    message: string,
  ) {
    super(message);
    this.name = 'EquipoError';
  }
}

async function leer<T>(res: Response): Promise<T> {
  const data = (await res.json().catch(() => null)) as
    | ({ response?: string; error_type?: string; message?: string } & T)
    | null;
  if (!res.ok || data?.response === 'error') {
    throw new EquipoError(data?.error_type ?? `http_${res.status}`, data?.message ?? 'No se pudo completar.');
  }
  if (!data) throw new EquipoError('red', 'Respuesta vacía del servidor.');
  return data;
}

interface LinkWire {
  token: string;
  expira_at: string;
  usos: number;
  estado: 'vigente' | 'vencido';
}
const link = (w: LinkWire): LinkEquipo => ({ token: w.token, expiraAt: w.expira_at, usos: w.usos, estado: w.estado });

interface PermisosWire {
  puede_publicar_en_tienda: boolean;
  marcas: string[] | null;
}
const permisos = (w: PermisosWire | undefined): MisPermisos =>
  w ? { puedePublicarEnTienda: w.puede_publicar_en_tienda, marcas: w.marcas } : { puedePublicarEnTienda: true, marcas: null };

export async function fetchEquipo(): Promise<Equipo> {
  const data = await leer<{
    soy_dueno: boolean;
    tope: number | null;
    incluye_equipo?: boolean;
    miembros: {
      id: string;
      nombre: string;
      email: string | null;
      es_dueno: boolean;
      unido_at: string | null;
      permisos?: PermisosWire;
    }[];
    link: LinkWire | null;
    marcas?: MarcaDeLaCuenta[] | null;
  }>(await atlasBackendRequest('GET', '/v1/equipo'));
  return {
    soyDueno: data.soy_dueno,
    tope: data.tope ?? null,
    incluyeEquipo: typeof data.incluye_equipo === 'boolean' ? data.incluye_equipo : null,
    miembros: data.miembros.map((m) => ({
      id: m.id,
      nombre: m.nombre,
      email: m.email,
      esDueno: m.es_dueno,
      unidoAt: m.unido_at,
      permisos: permisos(m.permisos),
    })),
    link: data.link ? link(data.link) : null,
    marcas: data.marcas ?? null,
  };
}

export async function crearLinkEquipo(): Promise<LinkEquipo> {
  const data = await leer<{ link: LinkWire }>(await atlasBackendRequest('POST', '/v1/equipo/link', {}));
  return link(data.link);
}

export async function desactivarLinkEquipo(): Promise<void> {
  await leer(await atlasBackendRequest('DELETE', '/v1/equipo/link'));
}

// Las tres mutaciones que cambian quién es del equipo vacían la caché de nombres de
// "Generada por": si no, la etiqueta seguiría mostrando (u omitiendo) a alguien hasta
// recargar la pestaña.
export async function sacarMiembro(userId: string): Promise<void> {
  await leer(await atlasBackendRequest('DELETE', `/v1/equipo/miembro/${encodeURIComponent(userId)}`));
  olvidarNombresDelEquipo();
}

export async function salirDelEquipo(): Promise<void> {
  await leer(await atlasBackendRequest('POST', '/v1/equipo/salir', {}));
  olvidarNombresDelEquipo();
}

/** Sólo el dueño. `marcas: null` = todas. */
export async function guardarPermisosDeMiembro(userId: string, p: MisPermisos): Promise<MisPermisos> {
  const data = await leer<{ permisos: PermisosWire }>(
    await atlasBackendRequest('PUT', `/v1/equipo/miembro/${encodeURIComponent(userId)}/permisos`, {
      puede_publicar_en_tienda: p.puedePublicarEnTienda,
      marcas: p.marcas,
    }),
  );
  olvidarPermisos();
  return permisos(data.permisos);
}

/** `plan_sin_equipo`: la cuenta que invitó ya no tiene un plan de equipo (bajó, o es un Ultra). */
export type MotivoInvitacionInvalida = 'inexistente' | 'vencido' | 'revocado' | 'equipo_lleno' | 'plan_sin_equipo';

export type InvitacionPublica =
  | { valido: true; nombreMarca: string; yaEsMiembro: boolean }
  | { valido: false; motivo: MotivoInvitacionInvalida };

/** Público: no exige sesión, pero si hay una la manda para saber si ya sos de esa empresa. */
export async function verInvitacion(token: string): Promise<InvitacionPublica> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const res = await fetch(`${ATLAS_BACKEND_URL}/v1/invitacion/${encodeURIComponent(token)}`, {
    headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
  });
  if (!res.ok) throw new EquipoError('red', 'No pudimos conectarte. Probá de nuevo.');
  const data = (await res.json()) as
    | { valido: true; nombre_marca: string; ya_es_miembro: boolean }
    | { valido: false; motivo: MotivoInvitacionInvalida };
  return data.valido
    ? { valido: true, nombreMarca: data.nombre_marca, yaEsMiembro: data.ya_es_miembro }
    : { valido: false, motivo: data.motivo };
}

export async function aceptarInvitacion(
  token: string,
): Promise<{ idCompany: string; nombreMarca: string; yaEraMiembro: boolean }> {
  const data = await leer<{ id_company: string; nombre_marca: string; ya_era_miembro: boolean }>(
    await atlasBackendRequest('POST', `/v1/invitacion/${encodeURIComponent(token)}/aceptar`, {}),
  );
  olvidarNombresDelEquipo();
  return { idCompany: data.id_company, nombreMarca: data.nombre_marca, yaEraMiembro: data.ya_era_miembro };
}

export function rutaUnirme(token: string): string {
  return `/unirme/${token}`;
}

/** La URL que el dueño copia: el origin actual, así sirve igual en local y en producción. */
export function urlDeInvitacion(token: string): string {
  return `${window.location.origin}${rutaUnirme(token)}`;
}
